/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import {useFlags} from 'launchdarkly-react-client-sdk';
import React, {useState} from 'react';
import {AgentPhoneNumbers} from './AgentPhoneNumbers';
import {FeatureFlagSet, FeatureFlags} from '../../../flags';

type AgentSettingsProps = {
  agentId: string | undefined;
  isDefault: boolean;
  setIsDefault: (value: boolean) => void;
  onDelete: () => void;
  canDelete: boolean;
  agentPhoneNumbers?: string[];
  onPhoneNumbersChange: (phoneNumbers: string[]) => void;
};

export const AgentSettings: React.FC<AgentSettingsProps> = ({
  agentId,
  isDefault,
  setIsDefault,
  onDelete,
  canDelete,
  agentPhoneNumbers = [],
  onPhoneNumbersChange,
}) => {
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const flags = useFlags<FeatureFlagSet>();
  const showPhoneNumbers = flags[FeatureFlags.testAgent];

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Stack direction={'row'} alignItems={'center'} spacing={2}>
            <Typography variant={'body1'}>Default Agent</Typography>
            <Switch checked={isDefault} onChange={() => setIsDefault(!isDefault)} color={'primary'} />
          </Stack>
          <Typography variant={'caption'} sx={{mt: 1, color: 'text.secondary'}}>
            Default agents will be used for all incoming user conversations when no specific agent is identified by
            phone number, agent ID, or other routing criteria. This ensures every user request is handled even without
            explicit agent selection.
          </Typography>
        </CardContent>
      </Card>

      {showPhoneNumbers && (
        <AgentPhoneNumbers
          agentPhoneNumbers={agentPhoneNumbers}
          onPhoneNumbersChange={onPhoneNumbersChange}
          agentId={agentId}
        />
      )}

      <Box textAlign={'right'}>
        <Button variant={'text'} color={'error'} onClick={() => setConfirmDeleteModalOpen(true)} disabled={!canDelete}>
          Delete Agent
        </Button>
      </Box>

      <Dialog open={confirmDeleteModalOpen} onClose={() => setConfirmDeleteModalOpen(false)}>
        <DialogTitle>Delete Agent</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this agent? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteModalOpen(false)}>Cancel</Button>
          <Button
            variant={'contained'}
            color={'error'}
            onClick={() => {
              onDelete();
              setConfirmDeleteModalOpen(false);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
